import React from 'react';

import {useTheme} from '@emotion/react';
import {Circle} from '@mui/icons-material';
import {
  SvgIcon,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {IconCreditCard} from '@zoom/icons-react';
import {
  Box,
  Stack,
} from '@zoom/react-ui';
import {IconChevronRightSmallFill} from '@zoom/sun-ui-icon';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useAppSelector} from 'Store/Store';

import {GlobalItemContainer, TypographyH4} from './Style';

// import {ReactComponent as ChevronRight} from 'assets/svg/chevron-right-filled.svg';
import {ReactComponent as ClockOutlined} from 'assets/svg/clock-outlined.svg';
import {ReactComponent as ProfileIcon} from 'assets/svg/profile-outlined.svg';
import {ReactComponent as TeamIcon} from 'assets/svg/team-outlined.svg';
import OverflowTip from 'Components/OverflowTooltip';
import {TemplatePreview} from 'Components/TemplatePreview/TemplatePreview';
import {resetApptStore} from 'Store/BookAppointmentsStore';
import {getSafeScheduleColorValue} from 'Utils';
import {getCapacityTypeText, getPrice, renderDuration} from 'Utils/CardDisplay';
import zoomPalette from 'zoomPalette';

/**
 * List Appointments Landing Page Component
 * @param {Object} props
 * @param {Object} props.appointment
 * @param {String} props.calendarId
 * @return {ReactElement}
 */
export default function GlobalLandingItem(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    appointment,
    calendarId,
  } = props;

  const color = getSafeScheduleColorValue(appointment?.color);

  const {t} = useTranslation();

  const handleClick = () => {
    // clear existing appt data before navigating to the next page
    dispatch(resetApptStore());
    // try to use server-generated link first to support vanity URL
    if (appointment?.bookingLink) {
      const {pathname, search} = new URL(appointment.bookingLink);
      navigate({
        pathname,
        search,
      });
    } else if (appointment) {
      navigate(`/${calendarId}/${appointment.slug || appointment.id}`);
    }
  };

  const theme = useTheme();

  const themeFontFace = useAppSelector((state) => state.themeState.fontFaceSrcMap?.[state.themeState.fontFamily]);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const overflowTipPlacement = (isSmallScreen) ? 'bottom' : 'right';

  const price = getPrice(appointment);

  return (
    <GlobalItemContainer
      tabIndex={0}
      onClick={handleClick}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        width: '100%',
        fontSize: '1rem',
      }}>
        <Box sx={{display: 'flex'}}>
          {color && <Circle sx={{color: color, fontSize: 24, mr: '10px', flexShrink: 0}} />}
          <OverflowTip
            lineClamp={2}
            content={appointment.summary || t('common.noTitle')}
            render={TypographyH4}
            placement={overflowTipPlacement}
            sx={{marginTop: '0'}}
          />
        </Box>
        <IconChevronRightSmallFill sx={{mt: '4px'}} />
      </Box>

      <Box sx={{
        flexGrow: 1,
        overflowY: 'auto',
      }}>
        <Stack direction="row" spacing="12px">
          <Stack direction="row" alignItems="center" spacing={1}>
            <SvgIcon
              fontSize="inherit"
              component={ClockOutlined}
              inheritViewBox
            />
            <Typography variant="body2">
              {renderDuration(appointment?.duration, t)}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <SvgIcon
              fontSize="inherit"
              component={appointment?.capacity === 1 ? ProfileIcon : TeamIcon}
              inheritViewBox
              sx={{marginLeft: '10px'}}
            />
            <Typography variant="body2">
              {t(getCapacityTypeText(appointment))}
            </Typography>
          </Stack>
          {price && <Stack direction="row" alignItems="center" spacing={1}>
            <IconCreditCard style={{fontSize: '16px', lineHeight: '1.25rem', position: 'relative', top: '2px'}} />
            <Typography variant="body2">
              {price}
            </Typography>
          </Stack>}
        </Stack>
        {
          appointment?.description &&
          <Box sx={{marginTop: '10px'}}>
            <TemplatePreview
              isBig={false}
              hideBackground
              noMargin
              fontSize={14}
              customFont={themeFontFace}
              color={zoomPalette.gray[70]}
              body={appointment.description}
              seamless={true}
            />
          </Box>
        }
      </Box>
    </GlobalItemContainer>
  );
}
